import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { WheelLanding, WheelOffer, WheelSegment } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { hasGoldPot, segmentLabel, wheelExplain, wheelLead, wheelStop } from '../../logic/minigames/wheel';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { animate, COLOURS, displayStyle, prefersReducedMotion } from '../board/ui';
import { glowTexture } from '../road/glow';
import { TurnGameDialog } from './TurnGameDialog';
import type { MiniGameDeps } from './types';
import type { GameView } from '../../logic/minigames/core';

/** Segment fills round the wheel: warm, no purple. The gold segment is gold. */
const FILLS = [0xe0a020, 0xb83a2a, 0x2f7a34, 0xf3e3c0];
const GOLD_FILL = 0xffd84a;
/** How fast the wheel turns while the server answers (radians a second). */
const IDLE_SPEED = 9;
/** The painted rim is drawn 2.28r across and its opening is 0.754 of that
 *  half-width (wheel/outer_rim.png), so the face shows out to about 0.86r.
 *  Every label, icon included, stays inside this radius, clear of the rim. */
const LABEL_SAFE = 0.8;
/** Where a label sits when it fits there, as a fraction of r. */
const LABEL_AT = 0.64;
/** Labels stay outside the hub (drawn 0.5r across) and a little more. */
const LABEL_HUB = 0.3;

/**
 * The wheel: eight segments of coins, free spins and the gold segment.
 * SPIN starts it turning while the server says where it stops; it eases to
 * a stop on that segment, ticking past each peg, and only then shows what
 * it paid. Tapping the wheel says what it does.
 */
export class WheelDialog extends TurnGameDialog<WheelOffer, WheelLanding, WheelLanding> {
  private readonly wheel = new Container();
  private readonly wedges = new Graphics();
  /** A soft shadow just inside the painted rim, over the wedges. */
  private readonly shading = new Graphics();
  /** The segments' words and icons: above the shading, so they stay
   *  bright, and turned with the wedges ({@link turn}). */
  private readonly labels = new Container();
  private readonly rim = new Sprite();
  private readonly hub = new Sprite();
  private readonly pointer = new Sprite();
  private readonly drawnPointer = new Graphics();
  private readonly glow: Sprite;
  private segments: readonly WheelSegment[] = [];
  private spinning = false;
  private lastPeg = 0;

  constructor(deps: MiniGameDeps) {
    super(deps, 'wheel', 'LUCKY WHEEL');
    this.rim.anchor.set(0.5);
    this.hub.anchor.set(0.5);
    this.pointer.anchor.set(0.5, 0.15);
    this.glow = new Sprite(glowTexture());
    this.glow.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.glow.tint = 0xffe08a;
    this.glow.visible = false;
    this.wheel.addChild(this.wedges);
    this.labels.eventMode = 'none';
    this.wheel.eventMode = 'static';
    this.wheel.cursor = 'help';
    this.wheel.on('pointertap', () => this.explain(wheelExplain(this.segments), { x: this.wheel.x, y: this.wheel.y - 40 }));
    this.shading.eventMode = 'none';
    this.content.addChild(this.glow, this.wheel, this.shading, this.labels, this.rim, this.hub, this.drawnPointer, this.pointer);
  }

  protected begin(offer: WheelOffer): void {
    this.segments = offer.setup?.segments ?? [];
    this.turn(0);
    this.spinning = false;
    this.settling = false;
    this.glow.visible = false;
  }

  protected onPiece(): void {}

  protected override actionLabel(): string | null {
    return 'SPIN';
  }

  protected override onActionTap(): void {
    if (this.spinning || this.game.phase !== 'play') return;
    this.spinning = true;
    this.settling = true;
    this.act('spin');
  }

  protected override onAnswered(_prev: GameView<WheelLanding, WheelLanding>, next: GameView<WheelLanding, WheelLanding>): void {
    const landing = next.state;
    if (!landing || next.phase !== 'over') {
      // Refused or lost: stop where it is and let the player try again.
      this.spinning = false;
      this.settling = false;
      return;
    }
    void this.stopOn(landing.segment);
  }

  /** Ease from the current angle to the landing segment, ticking past pegs. */
  private async stopOn(index: number): Promise<void> {
    this.spinning = false;
    const n = this.segments.length || 8;
    const from = this.wheel.rotation;
    const to = wheelStop(index, n, from, prefersReducedMotion() ? 0 : 3);
    const step = (Math.PI * 2) / n;
    await animate(prefersReducedMotion() ? 0 : 2800, (t) => {
      const k = 1 - Math.pow(1 - t, 3);
      this.turn(from + (to - from) * k);
      const peg = Math.floor((this.wheel.rotation + step / 2) / step);
      if (peg !== this.lastPeg) {
        this.lastPeg = peg;
        AudioBus.shared.play(GAME_SOUNDS.wheelTick, { volume: 0.35 });
      }
    });
    AudioBus.shared.play(GAME_SOUNDS.wheelLand, { volume: 0.8 });
    this.glow.visible = true;
    void animate(500, (t) => (this.glow.alpha = 0.9 * Math.sin(t * Math.PI * 0.5)));
    this.reveal();
  }

  protected override animate(deltaMs: number): void {
    if (this.spinning) this.turn(this.wheel.rotation + (IDLE_SPEED * deltaMs) / 1000);
  }

  /** Turn the wheel's face (wedges and labels together) to `angle`. */
  private turn(angle: number): void {
    this.wheel.rotation = angle;
    this.labels.rotation = angle;
  }

  protected leadText(): string {
    return this.settling && this.game.phase === 'over' ? 'Round it goes…' : wheelLead(this.game, hasGoldPot(this.segments));
  }

  protected pieceLabels(): string[] {
    return this.segments.map((s, i) => {
      const landed = this.game.phase === 'over' && !this.settling && this.game.state?.segment === i;
      return `Segment ${i + 1}: ${segmentLabel(s)}${landed ? ' (landed)' : ''}`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const size = Math.max(180, Math.min(width, 300, maxHeight - 18));
    const r = Math.round(size * 0.42);
    const cx = width / 2;
    const cy = y + size / 2 + 6;
    this.wheel.position.set(cx, cy);
    this.labels.position.set(cx, cy);
    this.drawWheel(r);
    this.drawShading(cx, cy, r);
    const rimTex = this.deps.texture('road:wheel_rim');
    this.rim.visible = !!rimTex;
    if (rimTex) {
      this.rim.texture = rimTex;
      this.rim.width = this.rim.height = r * 2.28;
      this.rim.position.set(cx, cy);
    }
    const hubTex = this.deps.texture('road:wheel_hub');
    this.hub.visible = !!hubTex;
    if (hubTex) {
      this.hub.texture = hubTex;
      this.hub.width = this.hub.height = r * 0.5;
      this.hub.position.set(cx, cy);
    }
    const pointerTex = this.deps.texture('road:wheel_pointer');
    this.pointer.visible = !!pointerTex;
    this.drawnPointer.clear();
    if (pointerTex) {
      this.pointer.texture = pointerTex;
      this.pointer.width = r * 0.3;
      this.pointer.height = r * 0.3;
      this.pointer.position.set(cx, cy - r - r * 0.16);
    } else {
      this.drawnPointer
        .poly([cx - 14, cy - r - 18, cx + 14, cy - r - 18, cx, cy - r + 8])
        .fill({ color: COLOURS.gold })
        .stroke({ color: COLOURS.ink, width: 2 });
    }
    this.glow.position.set(cx, cy - r * 0.62);
    this.glow.width = this.glow.height = r * 0.9;
    return y + size + 12;
  }

  private drawShading(cx: number, cy: number, r: number): void {
    // A soft shadow just inside the rim, so the face sits under it.
    this.shading.clear().circle(cx, cy, r - 3).stroke({ color: 0x1e0e02, alpha: 0.3, width: 6 });
  }

  private drawWheel(r: number): void {
    const n = this.segments.length || 8;
    const step = (Math.PI * 2) / n;
    this.wedges.clear();
    this.labels.removeChildren().forEach((c) => c.destroy());
    this.segments.forEach((s, i) => {
      // Segment 0 is centred at the top (angle -90 degrees).
      const a0 = -Math.PI / 2 + (i - 0.5) * step;
      const a1 = a0 + step;
      const fill = s.kind === 'gold' ? GOLD_FILL : FILLS[i % FILLS.length]!;
      this.wedges.moveTo(0, 0).arc(0, 0, r, a0, a1).lineTo(0, 0).fill({ color: fill }).stroke({ color: 0x5a3210, width: 2 });
      const mid = a0 + step / 2;
      const label = new Container();
      const icon = s.kind === 'fs' ? this.deps.texture('shared:icon:spin') : s.goldPot ? this.deps.texture('road:pot_gold') : null;
      const light = fill === 0xf3e3c0 || fill === GOLD_FILL || fill === 0xe0a020;
      const text = new Text({
        text: segmentLabel(s),
        style: displayStyle(Math.max(12, Math.round(r * (s.kind === 'coins' ? 0.15 : 0.11))), light ? 0x3a1a00 : COLOURS.gold),
      });
      if (light) text.style.stroke = { color: 0xfff4d0, width: 2, join: 'round' };
      text.anchor.set(0.5);
      label.addChild(text);
      if (icon) {
        // Words outside, where the wedge is widest and level with the coin
        // numbers; the icon inside them, where its small square still fits.
        const sp = new Sprite(icon);
        sp.anchor.set(0.5);
        sp.width = sp.height = r * 0.2;
        sp.y = r * 0.13;
        text.y = -r * 0.04;
        label.addChild(sp);
      }
      label.rotation = mid + Math.PI / 2;
      this.placeLabel(label, mid, r);
      this.labels.addChild(label);
    });
  }

  /**
   * Put a segment's label on its centre line, as far out as {@link LABEL_AT}
   * but no further than keeps every corner inside {@link LABEL_SAFE}, so no
   * word or icon sits under the painted rim. A label too big to fit between
   * the rim and the hub is scaled down. The label's -y points outwards.
   */
  private placeLabel(label: Container, mid: number, r: number): void {
    const b = label.getLocalBounds();
    const safe = r * LABEL_SAFE;
    let scale = 1;
    let d = r * LABEL_AT;
    for (let k = 0; k < 8; k++) {
      const half = Math.max(-b.minX, b.maxX) * scale;
      d = Math.min(r * LABEL_AT, Math.sqrt(Math.max(0, safe * safe - half * half)) + b.minY * scale);
      if (d - b.maxY * scale >= r * LABEL_HUB) break;
      scale *= 0.92;
    }
    label.scale.set(scale);
    label.position.set(Math.cos(mid) * d, Math.sin(mid) * d);
  }
}
